// Auth slice — account system (19BN Phase 1).
//
// Design: interface-stable slice bridging the anonymous-id system (auth/anon-id.ts)
// with the real /auth/* backend. login/logout/initializeAuth now talk to the
// ingest worker's auth endpoints; the slice shape stays compatible with the
// @extensible contract from 19BM (only implementations were swapped).
//
// Invariants:
//   - initializeAuth() is called once at app mount (useAuthInit hook)
//   - currentUser is null until initializeAuth runs
//   - resetCourse() does NOT touch auth state (cross-course global)
//   - login/logout are async (network calls); UI guards on isAuthLoading
//   - Anonymous fallback never throws — app always boots to a usable state
//
// @extensible — future OAuth (Phase 19BO+) swaps login impl, shape stays.

import type { StateCreator } from 'zustand';

export interface AuthUser {
  /** Stable user ID — anon ID when anonymous, real UUID when authenticated. */
  id: string;
  /** Email — undefined for anonymous users (no email bound). */
  email?: string;
  /** Display name — '' for anonymous (resolved at render via i18n), real name when authenticated. */
  displayName: string;
  /** Avatar URL — null for anonymous / users without a custom avatar. */
  avatarUrl: string | null;
  /** True when using anonymous (no real account). */
  isAnonymous: boolean;
}

/** Credentials accepted by login(). Narrowed from `unknown` in 19BN Phase 1. */
export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthSlice {
  // ── State ──────────────────────────────────────────────────────────────
  /** Current user. Null until initializeAuth() runs. */
  currentUser: AuthUser | null;
  /** True while auth is initializing (prevents flash of "logged out"). */
  isAuthLoading: boolean;

  // ── Actions ────────────────────────────────────────────────────────────
  /**
   * Probe /auth/me for an existing session. If found, populate currentUser
   * with the real user; otherwise fall back to anonymous. Called once at app
   * mount. Idempotent — subsequent calls are no-ops while loading.
   */
  initializeAuth: () => void;
  /**
   * Email + password login. Calls /auth/login, sets currentUser on success.
   * Throws IngestApiError on wrong credentials (caller shows inline error).
   */
  login: (credentials?: LoginCredentials) => Promise<void>;
  /**
   * Log out: call /auth/logout to clear the server session, then reset
   * currentUser to anonymous. Never throws — logout must always succeed
   * client-side even if the server is unreachable.
   */
  logout: () => Promise<void>;
  /**
   * Set currentUser directly (used after signup/magic-link verify, which
   * already have the user object from the response). Bypasses login().
   */
  setCurrentUser: (user: AuthUser) => void;
  /**
   * 19BO+ Phase B: re-probe /auth/me and refresh currentUser from the server.
   * Used after signup-verify (and any future flow that sets the session cookie
   * out-of-band, e.g. OAuth redirect). Unlike initializeAuth this is NOT
   * idempotent — it always re-probes, and on a successful session it triggers
   * the same side effects as login (anon→email migration + login notification),
   * closing the bug where the old single-stage signup's setCurrentUser skipped
   * them. Never throws — on failure falls back to anonymous.
   */
  refreshFromServer: () => Promise<void>;
  /**
   * Partial update of currentUser fields (display name, avatar). Local-only
   * for now; Phase 6 syncs to /auth/profile.
   */
  updateProfile: (data: Partial<AuthUser>) => void;
}

export const createAuthSlice: StateCreator<AuthSlice> = (set, get) => ({
  currentUser: null,
  isAuthLoading: true,

  initializeAuth: () => {
    // Idempotent guard — only run until loading completes.
    if (!get().isAuthLoading) return;
    import('../../api/auth.js')
      .then(({ authApi }) => authApi.me())
      .then((result) => {
        if (result?.user) {
          set({ currentUser: result.user, isAuthLoading: false });
          // 19BN Phase 3: trigger migration for existing session (e.g. page refresh).
          void triggerMigrateAnon();
        } else {
          // No session — fall back to anonymous.
          return import('../../auth/anon-id.js').then(({ getOrCreateAnonId }) => {
            const anonId = getOrCreateAnonId();
            set({
              currentUser: { id: anonId, displayName: '', avatarUrl: null, isAnonymous: true },
              isAuthLoading: false,
            });
          });
        }
      })
      .catch(() => {
        // Network error / worker down — boot to anonymous, don't block the app.
        import('../../auth/anon-id.js')
          .then(({ getOrCreateAnonId }) => {
            const anonId = getOrCreateAnonId();
            set({
              currentUser: { id: anonId, displayName: '', avatarUrl: null, isAnonymous: true },
              isAuthLoading: false,
            });
          })
          .catch(() => {
            // anon-id also failed (SSR / test) — unblock loading with bare anon.
            set({
              currentUser: { id: 'anon', displayName: '', avatarUrl: null, isAnonymous: true },
              isAuthLoading: false,
            });
          });
      });
  },

  login: async (credentials?: LoginCredentials) => {
    if (!credentials) return;
    const { authApi } = await import('../../api/auth.js');
    const result = await authApi.login(credentials.email, credentials.password);
    set({ currentUser: result.user });
    // 19BN Phase 5: notify login.
    try {
      const state = get() as { pushNotification?: (n: { source: string; level: string; title: string; message?: string; persistent: boolean }) => string };
      state.pushNotification?.({ source: 'system', level: 'info', title: 'Signed in', message: result.user.email, persistent: false });
    } catch { /* non-critical */ }
    // 19BN Phase 3: trigger anon→email migration after login (best-effort).
    void triggerMigrateAnon();
  },

  logout: async () => {
    // Best-effort server logout — never throw (client reset is the contract).
    try {
      const { authApi } = await import('../../api/auth.js');
      await authApi.logout();
    } catch {
      // server unreachable — proceed with local reset anyway
    }
    // 19BN Phase 2: clear account state on logout.
    try {
      const { clearAccount } = get() as { clearAccount?: () => void };
      clearAccount?.();
    } catch {
      // account-slice not wired (shouldn't happen, but defensive)
    }
    const { getOrCreateAnonId } = await import('../../auth/anon-id.js');
    const anonId = getOrCreateAnonId();
    set({
      currentUser: { id: anonId, displayName: '', avatarUrl: null, isAnonymous: true },
    });
    // 19BN Phase 5: notify logout.
    try {
      const state = get() as { pushNotification?: (n: { source: string; level: string; title: string; persistent: boolean }) => string };
      state.pushNotification?.({ source: 'system', level: 'info', title: 'Signed out', persistent: false });
    } catch { /* non-critical */ }
  },

  setCurrentUser: (user) => {
    set({ currentUser: user });
  },

  refreshFromServer: async () => {
    try {
      const { authApi } = await import('../../api/auth.js');
      const result = await authApi.me();
      if (result?.user) {
        set({ currentUser: result.user });
        // Same side effects as login() — closes the bug where the old
        // single-stage signup's setCurrentUser skipped migration + notification.
        try {
          const state = get() as { pushNotification?: (n: { source: string; level: string; title: string; message?: string; persistent: boolean }) => string };
          state.pushNotification?.({ source: 'system', level: 'info', title: 'Signed in', message: result.user.email, persistent: false });
        } catch { /* non-critical */ }
        void triggerMigrateAnon();
      }
    } catch {
      // Network error — leave currentUser as-is (don't downgrade to anon on
      // a transient blip; the next /auth/me probe on reload will correct).
    }
  },

  updateProfile: (data) => {
    const current = get().currentUser;
    if (!current) return;
    set({ currentUser: { ...current, ...data } });
  },
});

/**
 * 19BN Phase 3: trigger anon→email migration after login.
 * Best-effort: reads local IDB drafts + anonId, sends to /sync/migrate-anon.
 * Failures are swallowed (migration is non-blocking; user can retry).
 */
async function triggerMigrateAnon(): Promise<void> {
  try {
    const { getOrCreateAnonId } = await import('../../auth/anon-id.js');
    const anonId = getOrCreateAnonId();
    // Read local drafts (lightweight metadata only — not results/PDFs).
    const { listDrafts } = await import('../../storage/idb.js');
    const drafts = await listDrafts();
    if (drafts.length === 0) return; // nothing to migrate
    const { syncApi } = await import('../../api/sync.js');
    const result = await syncApi.migrateAnon(anonId, drafts as Array<Record<string, unknown>>);
    if (result.migratedCount > 0) {
      // eslint-disable-next-line no-console -- anon→cloud migration audit — intentional production log
      console.log(`[migrate-anon] ${result.migratedCount} courses synced to cloud`);
    }
  } catch {
    // non-critical — user data is still local; migration can retry on next login
  }
}
